import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { Lead } from '../../../models/Outreach/types'
import { useAptConfig } from '../../../hooks/useAptConfig'
import { usePremiums } from '../../../hooks/usePremiums'
import { useServedSchools } from '../../../hooks/useServedSchools'
import { useUpdateLeadPreferences } from '../../../hooks/Outreach/useLeadCrm'
import { useUser } from '../../../hooks/useUser'

function dateInput(value: string | null | undefined): string {
    if (!value) return ''
    return value.slice(0, 10)
}

interface Props {
    open: boolean
    lead: Lead
    onClose: () => void
}

export const EditPreferencesDialog: React.FC<Props> = ({
    open,
    lead,
    onClose,
}) => {
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id
    const { unitConfigList } = useAptConfig({ unitConfigList: true })
    const { premiums } = usePremiums()
    const { servedSchools } = useServedSchools(apartmentId)
    const { updatePreferences, isSaving } = useUpdateLeadPreferences(lead.id)

    const [unitConfigId, setUnitConfigId] = useState<number | ''>('')
    const [budget, setBudget] = useState('')
    const [moveIn, setMoveIn] = useState('')
    const [moveOut, setMoveOut] = useState('')
    const [schoolId, setSchoolId] = useState<number | ''>('')
    const [premiumIds, setPremiumIds] = useState<number[]>([])
    const [roommates, setRoommates] = useState('')
    const [requests, setRequests] = useState('')

    useEffect(() => {
        if (!open) return
        setUnitConfigId(lead.preferred_unit_config?.id ?? '')
        setBudget(
            lead.preferred_rent_cost != null
                ? String(lead.preferred_rent_cost)
                : '',
        )
        setMoveIn(dateInput(lead.preferred_move_in_date))
        setMoveOut(dateInput(lead.preferred_move_out_date))
        setSchoolId(lead.school_attendance ?? '')
        setPremiumIds((lead.preferred_premiums ?? []).map((row) => row.id))
        setRoommates(
            (lead.preferred_roommates ?? [])
                .map((row) => row.name)
                .filter(Boolean)
                .join(', '),
        )
        setRequests(lead.additional_requests || '')
    }, [open, lead])

    const floorPlans = (unitConfigList ?? []).filter(
        (config) => config.shows_on_schedule,
    )
    const activePremiums = (premiums ?? []).filter((row) => row.is_active)
    const schools = (servedSchools?.schools ?? []).filter(
        (row) => row.is_served,
    )

    const handleSave = async () => {
        const names = roommates
            .split(',')
            .map((name) => name.trim())
            .filter(Boolean)
        const rent = budget.trim() === '' ? null : Number(budget)
        try {
            await updatePreferences({
                preferred_unit_config_id:
                    unitConfigId === '' ? null : Number(unitConfigId),
                preferred_rent_cost:
                    rent == null || Number.isNaN(rent) ? null : rent,
                preferred_move_in_date: moveIn || null,
                preferred_move_out_date: moveOut || null,
                school_attendance_id: schoolId === '' ? null : Number(schoolId),
                additional_requests: requests,
                preferred_premium_ids: premiumIds,
                roommate_names: names,
            })
            onClose()
        } catch {
            // Toast is raised by the mutation.
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle disableTypography>
                <Box display="flex" alignItems="center">
                    <Typography
                        variant="h6"
                        style={{ flex: 1, fontWeight: 700 }}
                    >
                        Edit preferences
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <FormControl fullWidth variant="outlined" margin="dense">
                    <InputLabel>Floor plan</InputLabel>
                    <Select
                        value={unitConfigId}
                        onChange={(event) =>
                            setUnitConfigId(event.target.value as number | '')
                        }
                        label="Floor plan"
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {floorPlans.map((config) => (
                            <MenuItem key={config.id} value={config.id}>
                                {config.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    label="Budget"
                    type="number"
                    value={budget}
                    onChange={(event) => setBudget(event.target.value)}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    label="Move-in"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={moveIn}
                    onChange={(event) => setMoveIn(event.target.value)}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    label="Move-out"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={moveOut}
                    onChange={(event) => setMoveOut(event.target.value)}
                />
                <FormControl fullWidth variant="outlined" margin="dense">
                    <InputLabel>School</InputLabel>
                    <Select
                        value={schoolId}
                        onChange={(event) =>
                            setSchoolId(event.target.value as number | '')
                        }
                        label="School"
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {schools.map((school) => (
                            <MenuItem
                                key={school.school_id}
                                value={school.school_id}
                            >
                                {school.effective_display_name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {activePremiums.length > 0 && (
                    <Box mt={1} mb={1}>
                        <Typography
                            variant="caption"
                            style={{ fontWeight: 700, letterSpacing: '0.04em' }}
                        >
                            PREMIUMS
                        </Typography>
                        <FormGroup>
                            {activePremiums.map((premium) => (
                                <FormControlLabel
                                    key={premium.id}
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={premiumIds.includes(
                                                premium.id,
                                            )}
                                            onChange={(event) => {
                                                if (event.target.checked) {
                                                    setPremiumIds((current) => [
                                                        ...current,
                                                        premium.id,
                                                    ])
                                                } else {
                                                    setPremiumIds((current) =>
                                                        current.filter(
                                                            (id) =>
                                                                id !==
                                                                premium.id,
                                                        ),
                                                    )
                                                }
                                            }}
                                        />
                                    }
                                    label={premium.name}
                                />
                            ))}
                        </FormGroup>
                    </Box>
                )}
                <TextField
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    label="Roommates"
                    placeholder="Names, comma separated"
                    value={roommates}
                    onChange={(event) => setRoommates(event.target.value)}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    label="Requests"
                    multiline
                    rows={3}
                    value={requests}
                    onChange={(event) => setRequests(event.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={isSaving}
                    onClick={handleSave}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}
